<template>
  <div
    :class="[$style['mini-cart'], {[$style['mini-cart--inline']]: inline}]"
    data-test-id="mini-cart"
    @mouseover="isInteracting"
    @mouseout="stoppedInteracting"
    @touchmove.passive="isInteracting"
    @touchend="stoppedInteracting">
    <div :class="$style['mini-cart__title']">
      <div>
        <h5 class="h3">
          {{ $t('checkout/shopping-bag') }}<br>
        </h5>
        <small class="h-color-grey-dark">{{ productsAmount }} {{ productsAmount > 1 ? 'items' : 'item' }}</small>
      </div>

      <Btn
        v-if="!inline"
        color="white"
        border
        size="small"
        data-test-id="mini-cart-close"
        @click="cartStore.show = false">
        {{ $t('general/close') }}
      </Btn>
    </div>
    <div :class="$style['mini-cart__content']">
      <TransitionGroup
        v-if="products.length"
        name="highlight-new"
        tag="div"
        class="mini-cart__items">
        <CartItem
          v-for="product in products"
          :key="product.cId + '-' + product.pId"
          compact
          :item="product" />
      </TransitionGroup>
      <div
        v-else
        class="h-margin-bottom h-padding">
        <small>{{ $t('checkout/shopping-bag-empty') }}</small>
      </div>
    </div>
    <div v-if="!inline" :class="$style['mini-cart__bottom']">
      <Btn
        shape="pill"
        full-width="all"
        color="cta"
        bold
        :postfix="`${total.replace('.', ',')}`"
        :to="{ name: $lang + '-cart' }">
        {{ $t('checkout/to-shoppingbag') }}
      </Btn>
    </div>
  </div>
</template>
<script setup>
const cartStore = useCartStore()
const timeout = ref(null)
const products = computed(() => cartStore.products)
const total = computed(() => cartStore.total)

defineProps({
  inline: {
    type: Boolean,
    default: false
  }
})

const isInteracting = () => {
  if (cartStore.show === false || !timeout.value) { return }
  clearTimeout(timeout.value)
  timeout.value = null
}

const stoppedInteracting = () => {
  if (cartStore.show === false) { return }
  timeout.value = setTimeout(() => {
    cartStore.show = false
  }, 5000)
}

const productsAmount = computed(() => {
  return cartStore.products.reduce((total, product) => {
    return total + product.quantity
  }, 0)
})

onMounted(async () => {
  await useI18n(['checkout'])
  if (timeout.value) {
    clearTimeout(timeout.value)
    timeout.value = null
  }
  timeout.value = setTimeout(() => {
    cartStore.show = false
  }, 5000)
})

onUnmounted(() => {
  if (timeout.value) {
    clearTimeout(timeout.value)
    timeout.value = null
  }
})

</script>
<style lang="scss" module>
.mini-cart {
  background-color: $white;
  color: $black;
  box-shadow: $box-shadow-large;
  border-radius: $border-radius;
  display: flex;
  flex-direction: column;
  font-family: $font-base;
  font-size: $font-size;
  width: $spacing * 60;
  height: auto;
  max-height: min(#{$spacing * 90}, calc(100vh - var(--toolbar-height)));
  position: fixed;
  top: var(--toolbar-height);
  padding-top: env(safe-area-inset-top);
  right: 0;
  z-index: z-index(cart);

  @media (max-width: #{$breakpoint-tablet}) {
    right: 0;
    top: 0;
    width: 100%;
    max-height: 100vh;
  }

  &__content {
    @include scrollbarVisible;
    overflow-y: auto;
    overscroll-behavior: none;
  }

  &__bottom {
    padding: $spacing * 2;
  }

  &__title {
    border-bottom: 1px solid $grey-lightest;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: $spacing * 2;

    @media (max-width: #{$breakpoint-tablet}) {
      padding: $spacing $spacing * 2;
    }
  }

  &--inline {
    position: relative;
    top: 0;
    width: 100%;
    z-index: 1;
  }
}

:global {
  .mini-cart-enter-active {
    transition: transform $transition-time-in $transition-ease-in;
  }

  .mini-cart-leave-active {
    transition: transform $transition-time-out $transition-ease-out;
  }

  .mini-cart-enter-from,
  .mini-cart-leave-to {
    transform: translateX(105%);
  }

  .mini-cart-leave-from,
  .mini-cart-enter-to {
    transform: translateX(0);
  }
}
</style>
